/**
 * DemoModeBanner — floating pill shown while a read-only demo session is active.
 * Explains why writes are blocked and drives sign-up conversion.
 *
 * Mounted app-wide in App.tsx (alongside EmulationBanner) so it persists across
 * routes. Renders nothing outside a demo session. Follows the EmulationBanner
 * fixed-pill pattern so it never disturbs the app's height-based layout. See
 * docs/platform/demo-without-login-design.md.
 */
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/button';
import { useAuth } from '../../auth';
import { isDemoMode } from '../../auth/demoSession';

export function DemoModeBanner() {
  const { t } = useTranslation('auth');
  const { logout } = useAuth();

  if (!isDemoMode()) return null;

  const handleExit = async () => {
    await logout();
    window.location.assign('/login');
  };

  // Leave the demo before heading to sign-up — otherwise PublicRoute treats the
  // still-present demo token as an authenticated session and bounces /register
  // back to the app home.
  const handleSignUp = async () => {
    await logout();
    window.location.assign('/register');
  };

  return (
    <div className="fixed top-2 left-1/2 z-[1300] -translate-x-1/2 flex max-w-[95vw] items-center gap-3 rounded-lg bg-primary px-4 py-1.5 text-primary-foreground shadow-lg">
      <Eye className="h-4 w-4 shrink-0" />
      <span className="text-sm font-medium">{t('demo.banner')}</span>
      <Button
        size="sm"
        variant="secondary"
        className="h-6 px-2 text-xs"
        onClick={handleSignUp}
        data-testid="demo-banner-signup"
      >
        {t('demo.bannerCta')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleExit}
        className="h-auto px-2 py-0 text-xs text-inherit hover:bg-primary-foreground/10"
        data-testid="demo-banner-exit"
      >
        {t('demo.exit')}
      </Button>
    </div>
  );
}
